'use client';

import dynamic from 'next/dynamic';
import { Users } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { capturePosthogEvent } from '@/lib/posthog';
import { withCrossDomainDistinctId } from '@/lib/posthog-cross-domain';
import StripeButton from './StripeButton';

// Three.js inside a Canvas can't SSR, and we don't want to gate first
// paint of the section on the WebGL bundle. Dynamic + ssr:false keeps
// the rest of the section SSR'd; the cube hydrates after.
const GroupCubeMock = dynamic(() => import('./GroupCubeMock'), {
  ssr: false,
  loading: () => <div className="w-full aspect-square max-w-md mx-auto" />,
});

export default function GroupQuizSection() {
  const posthog = usePostHog();
  return (
    <section className="font-inter py-20 md:py-28 bg-surface-alt">
      <div className="mx-auto px-5 lg:px-8" style={{ maxWidth: 'var(--max-w-landing)' }}>
        <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-16">
          {/* Visual: live 3D cube with 5 sample group members positioned
              across the macro regions. Drag to inspect.

              The `w-full` on this column is load-bearing, not cosmetic: the
              flex row uses items-center, so without an explicit width the
              column shrink-wraps to its content. When the cube is empty
              (SSR / before the ssr:false WebGL chunk mounts) that means zero
              width, so the aspect-square box below can't derive a height and
              the card collapses, then jumps ~300px when the cube mounts: a
              top driver of the 0.44 field CLS. A definite width here lets the
              square reserve its space in the initial HTML (same pattern as
              the working HeroCube column). */}
          <div className="flex-1 flex justify-center w-full">
            <div className="bg-surface rounded-2xl border border-border p-6 shadow-card w-full max-w-md">
              {/* Server-rendered wrapper: aspect-square derives its height
                  from its own (now definite) width, independent of children,
                  so the box exists before GroupCubeMock hydrates and holds
                  through every load phase. CLS = 0. */}
              <div className="w-full aspect-square">
                <GroupCubeMock />
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="flex-1 max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cta-secondary text-xs font-semibold text-text-secondary uppercase tracking-wider mb-5">
              <Users size={14} />
              Group mode
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-text tracking-tight mb-4">
              Take it with friends
            </h2>
            <p className="text-text-secondary text-lg leading-relaxed mb-6">
              Send one link to as many as seven friends. When they finish, you see
              where the group lands and who agrees with whom in ways nobody expected.
            </p>
            <ul className="space-y-3 text-text-secondary mb-8">
              <li className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted mt-2 shrink-0" />
                Everyone answers on their own phone, whenever they get to it.
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted mt-2 shrink-0" />
                All of you show up on one shared map, so the outliers are easy to spot.
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted mt-2 shrink-0" />
                The reveal points out the questions that split the group, which is usually where the conversation starts.
              </li>
            </ul>
            {/* Absolute https://votelyquiz.com so the marketing host
                (tryvotely.com) never serves the actual group-host flow.
                Product surface lives on votelyquiz.com. */}
            <StripeButton
              href="https://votelyquiz.com/group/new"
              onClick={(e) => {
                capturePosthogEvent(posthog, 'cta_start_clicked', { location: 'group_section' }, { sendToServer: true });
                const target = withCrossDomainDistinctId('https://votelyquiz.com/group/new', posthog);
                if (target !== 'https://votelyquiz.com/group/new') {
                  e.preventDefault();
                  window.location.assign(target);
                }
              }}
            >
              Start a group quiz
            </StripeButton>
          </div>
        </div>
      </div>
    </section>
  );
}
